import I18n from 'i18n-js';
import { I18N as Translate } from '@reverbdotcom/commons/src/components/translate';
import React from 'react';
import { useHelpCenterArticleUrl } from '@reverbdotcom/commons/src/url_helpers';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { RCFlex, RCIcon, RCRadioCardGroup, RCText, RCCheckbox } from '@reverbdotcom/cadence/components';
import { IRCCheckableProps } from '@reverbdotcom/cadence/components/forms/RCCheckbox/RCCheckbox';

import {
  BankIcon,
  ClockIcon,
  LightningDuotoneIcon,
  LockIcon,
  ReverbLogoCircleIcon,
  SmileyDuotoneIcon,
  TipJarDuotoneIcon,
} from '@reverbdotcom/cadence/icons/react';

import { trackEvent, MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { ReverbPaymentsPayoutMethod } from './payment_provider';
import ErrorMessage from './payout_method_error_message';

export function WalletValueProp() {
  const walletHcArticleLink = useHelpCenterArticleUrl(WALLET_HC_ID);

  return (
    <>
      <div className="mb-2">
        <RCFlex align="center">
          <div className="text-color-vu-orange-600">
            <RCIcon svgComponent={LightningDuotoneIcon} size="rc-space-6" />
          </div>
          <RCText htmlTag="span">
            <Translate
              html
              text="js.dashboard.selling.shopPolicies.directCheckoutProfile.reverbWalletAvailable.payoutMethodSelection.wallet.bulletPoint1"
            >
              <strong>
                {I18n.t('js.dashboard.selling.shopPolicies.directCheckoutProfile.reverbWalletAvailable.payoutMethodSelection.wallet.bulletPoint1Emphasis')}
              </strong>
            </Translate>
          </RCText>
        </RCFlex>
      </div>
      <div className="mb-2">
        <RCFlex align="center">
          <div className="text-color-vu-orange-600">
            <RCIcon svgComponent={TipJarDuotoneIcon} size="rc-space-6" />
          </div>
          <RCText htmlTag="span">
            <Translate
              html
              text="js.dashboard.selling.shopPolicies.directCheckoutProfile.reverbWalletAvailable.payoutMethodSelection.wallet.bulletPoint2"
            >
              <strong>
                {I18n.t('js.dashboard.selling.shopPolicies.directCheckoutProfile.reverbWalletAvailable.payoutMethodSelection.wallet.bulletPoint2Emphasis')}
              </strong>
            </Translate>
          </RCText>
        </RCFlex>
      </div>
      <div className="mb-2">
        <RCFlex align="center">
          <div className="text-color-vu-orange-600">
            <RCIcon svgComponent={SmileyDuotoneIcon} size="rc-space-6" />
          </div>
          <RCText htmlTag="span">
            <strong>
              {I18n.t('js.dashboard.selling.shopPolicies.directCheckoutProfile.reverbWalletAvailable.payoutMethodSelection.wallet.bulletPoint3')}
            </strong>
          </RCText>
        </RCFlex>
      </div>
      <div className="mt-3">
        <RCFlex align="center">
          <RCText htmlTag="span" size="350">
            <CoreLink
              to={walletHcArticleLink}
              target="_blank"
              className="text-color-primary td-underline"
            >
              {I18n.t('js.dashboard.selling.shopPolicies.directCheckoutProfile.reverbWalletAvailable.payoutMethodSelection.wallet.learnMore')}
            </CoreLink>
          </RCText>
        </RCFlex>
      </div>
    </>
  );
}

export function BankValueProp() {
  return (
    <>
      <div className="mb-2">
        <RCFlex align="center">
          <div className="text-color-blue-600">
            <RCIcon svgComponent={LockIcon} size="rc-space-6" />
          </div>
          <RCText htmlTag="span">
            <Translate
              html
              text="js.dashboard.selling.shopPolicies.directCheckoutProfile.reverbWalletAvailable.payoutMethodSelection.ach.depositEarnings"
            />
          </RCText>
        </RCFlex>
      </div>
      <div className="mb-3">
        <RCFlex align="center">
          <div className="text-color-blue-600">
            <RCIcon svgComponent={ClockIcon} size="rc-space-6" />
          </div>
          <RCText htmlTag="span">
            <Translate
              html
              text="js.dashboard.selling.shopPolicies.directCheckoutProfile.reverbWalletAvailable.payoutMethodSelection.ach.depositHowLong"
            />
          </RCText>
        </RCFlex>
      </div>
    </>
  );
}

export function WalletAgreeToTermsCheckBox({
  checked,
  onChange,
  errorText,
  disabled,
}: Omit<IRCCheckableProps, 'label'>) {
  return (
    <RCCheckbox
      label={
        <div className="ml-2">
          <RCText weight="regular" size="350">
            <Translate
              html
              text="js.dashboard.selling.shopPolicies.directCheckoutProfile.reverbWalletAvailable.payoutMethodSelection.wallet.terms.iAgree"
              args={{
                walletTermsLinkOpen: '<a href="https://reverb.com/legal/wallet-terms" target="_blank">',
                reverbTermsLinkOpen: '<a href="https://reverb.com/legal/reverbcom-terms-of-use" target="_blank">',
                privacyLinkOpen: '<a href="https://reverb.com/legal/reverbcom-privacy-policy" target="_blank">',
                linkClose: '</a>',
              }}
            />
          </RCText>
        </div>
      }
      checked={checked}
      onChange={onChange}
      errorText={errorText}
      disabled={disabled}
    />
  );
}

interface Props {
  payoutMethod: string;
  formPrefix: string;
  dcProfileComplete: boolean;
  hasDirectCheckoutProfileErrors: boolean;
  joinedDirectCheckoutProfileErrors: string;
  payoutBankAccountForm: React.ReactElement;
  clickPlaidButton: () => void;
  onPayoutMethodChange?: (selectedPayoutMethod: string) => void;
  onAcceptWalletTermsChange?: (accepted: boolean) => void;
  onPayoutMethodSaved?: (payoutMethod: string) => void;
  loading: boolean;
  selectedPayoutMethod?: string;
  setSelectedPayoutMethod?: (method: string) => void;
  acceptedWalletTerms?: boolean;
  setAcceptedWalletTerms?: (accepted: boolean) => void;
  acceptWalletErrorText?: string;
  setAcceptedWalletErrorText?: (text: string) => void;
}

export const WALLET_HC_ID = '43454547005075';
export const COMPONENT_NAME = 'WalletEligiblePayoutMethodSelection';

export function WalletEligiblePayoutMethodSelection(props: Props) {
  const {
    formPrefix,
    dcProfileComplete,
    hasDirectCheckoutProfileErrors,
    joinedDirectCheckoutProfileErrors,
    payoutBankAccountForm,
    setSelectedPayoutMethod,
    selectedPayoutMethod,
    setAcceptedWalletTerms,
    acceptedWalletTerms,
    setAcceptedWalletErrorText,
    acceptWalletErrorText,
    loading,
  } = props;


  React.useEffect(() => {
    trackEvent({
      eventName: MParticleEventName.ViewedWalletEligiblePayoutMethodSelection,
    });
  }, []);

  return (
    <>
      <RCRadioCardGroup
        id="payout-method-selection"
        name="payout-method-selection"
        onChange={(e) => {
          setSelectedPayoutMethod(e.target.value);
          props.onPayoutMethodChange(e.target.value);
          setAcceptedWalletTerms(false);
          props.onAcceptWalletTermsChange?.(false);
          setAcceptedWalletErrorText('');
        }}
        value={selectedPayoutMethod}
        iconPlacement="hidden"
        label={I18n.t('js.dashboard.selling.shopPolicies.directCheckoutProfile.reverbWalletAvailable.selectHowYouWantToGetPaid')}
        visuallyHiddenLabel
      >
        <div className="width-100 tablet-width-100">
          <RCRadioCardGroup.Option
            label={
              <RCFlex align="center">
                <div className="text-color-vu-orange-600">
                  <RCIcon svgComponent={ReverbLogoCircleIcon} size="rc-space-8" />
                </div>
                <RCText.Utility size="450" weight="bold" htmlTag="span">
                  {I18n.t('js.dashboard.selling.shopPolicies.directCheckoutProfile.reverbWalletAvailable.payoutMethodSelection.wallet.label')}
                </RCText.Utility>
              </RCFlex>
            }
            value={ReverbPaymentsPayoutMethod.Wallet}
            disabled={loading}
          >
            {(selectedPayoutMethod === ReverbPaymentsPayoutMethod.Wallet || selectedPayoutMethod === ReverbPaymentsPayoutMethod.ReverbBank ||
              !selectedPayoutMethod) && (
              <WalletValueProp />
            )}
          </RCRadioCardGroup.Option>
        </div>

        <div className="width-100 tablet-width-100">
          <RCRadioCardGroup.Option
            label={
              <RCFlex align="center">
                <div className="bg-blue bd-radius-full text-color-bw-white padding-1">
                  <RCIcon svgComponent={BankIcon} size="rc-space-6" />
                </div>
                <RCText.Utility size="450" weight="bold" htmlTag="span">
                  {I18n.t('js.dashboard.selling.shopPolicies.directCheckoutProfile.reverbWalletAvailable.payoutMethodSelection.ach.label')}
                </RCText.Utility>
              </RCFlex>
            }
            value={ReverbPaymentsPayoutMethod.ACH}
            disabled={loading}
          >
            {(selectedPayoutMethod === ReverbPaymentsPayoutMethod.ACH || selectedPayoutMethod === ReverbPaymentsPayoutMethod.ReverbBank ||
              !selectedPayoutMethod) && (
              <>
                <BankValueProp />

                {hasDirectCheckoutProfileErrors &&
                  <div>
                    <ErrorMessage message={joinedDirectCheckoutProfileErrors} />
                  </div>
                }
                {selectedPayoutMethod === ReverbPaymentsPayoutMethod.ACH &&
                  <div className="mt-5">
                    {React.cloneElement(payoutBankAccountForm, { selectedWalletEligiblePayoutMethod: selectedPayoutMethod })}
                  </div>
                }
              </>
            )}
          </RCRadioCardGroup.Option>
        </div>
        {selectedPayoutMethod === ReverbPaymentsPayoutMethod.Wallet && (
          <div className="wallet-accept-terms mt-2">
            <div className="mb-3">
              <RCText size="350" weight="bold">
                {I18n.t('js.dashboard.selling.shopPolicies.directCheckoutProfile.reverbWalletAvailable.payoutMethodSelection.wallet.disclaimer')}
              </RCText>
            </div>
            <WalletAgreeToTermsCheckBox
              checked={acceptedWalletTerms}
              onChange={() => {
                setAcceptedWalletErrorText('');
                setAcceptedWalletTerms(!acceptedWalletTerms);
                props.onAcceptWalletTermsChange?.(!acceptedWalletTerms);
              }}
              errorText={acceptWalletErrorText}
              disabled={selectedPayoutMethod !== ReverbPaymentsPayoutMethod.Wallet}
            />
          </div>

        )}
        {selectedPayoutMethod === ReverbPaymentsPayoutMethod.ACH && !dcProfileComplete && (
          <div className="mt-2 mb-1">
            <RCText htmlTag="span" weight="bold">
              <Translate
                html
                text="js.dashboard.selling.shopPolicies.directCheckoutProfile.reverbWalletAvailable.payoutMethodSelection.ach.connectWithPlaidEmphasis"
              >
              </Translate>
            </RCText>
            <RCText htmlTag="span">
              <Translate
                html
                text="js.dashboard.selling.shopPolicies.directCheckoutProfile.reverbWalletAvailable.payoutMethodSelection.ach.connectWithPlaid"
              >
              </Translate>
            </RCText>
            <RCText htmlTag="span" size="400">
              <CoreLink
                to="https://plaid.com/how-we-handle-data/"
                target="_blank"
                className="text-color-primary td-underline"
              >
                {I18n.t('js.dashboard.selling.shopPolicies.directCheckoutProfile.reverbWalletAvailable.payoutMethodSelection.ach.learnMore')}
              </CoreLink>
            </RCText>
          </div>
        )}
      </RCRadioCardGroup>

      <input
        value={selectedPayoutMethod || ''}
        type="hidden"
        name={`${formPrefix}[payout_method]`}
      />
    </>
  );
}
